import { FilterXSS, getDefaultWhiteList, IWhiteList, safeAttrValue as defaultSafeAttrValue } from 'xss';

import { ensureHtmlImageAlts } from './ensureHtmlImageAlts';

const DISALLOWED_TAGS = ['audio', 'video', 'area', 'details', 'summary'];

const SHARED_ATTRS = ['class', 'id', 'style'];

function buildWhiteList(): IWhiteList {
    const whiteList = getDefaultWhiteList();

    DISALLOWED_TAGS.forEach((tag) => {
        delete whiteList[tag];
    });

    Object.keys(whiteList).forEach((tag) => {
        const attrs = whiteList[tag] || [];
        SHARED_ATTRS.forEach((attr) => {
            if (!attrs.includes(attr)) {
                attrs.push(attr);
            }
        });
        whiteList[tag] = attrs;
    });

    if (whiteList.a && !whiteList.a.includes('rel')) {
        whiteList.a.push('rel');
    }

    return whiteList;
}

function isSchemelessRelativeUrl(value: string): boolean {
    const trimmed = value.trim();
    if (!trimmed || trimmed.includes(':')) {
        return false;
    }
    return true;
}

function safeAttrValue(
    tag: string,
    name: string,
    value: string,
    cssFilter: { process(value: string): string },
): string {
    const result = defaultSafeAttrValue(tag, name, value, cssFilter);
    if (result !== '' || (name !== 'href' && name !== 'src')) {
        return result;
    }

    if (!isSchemelessRelativeUrl(value)) {
        return '';
    }

    return defaultSafeAttrValue(tag, name, `/${value.trim()}`, cssFilter).replace(/^\//, '');
}

const cmsHtmlFilter = new FilterXSS({
    whiteList: buildWhiteList(),
    stripIgnoreTag: true,
    stripIgnoreTagBody: ['script', 'style', 'noscript', 'iframe', 'object', 'embed', 'form'],
    css: true,
    safeAttrValue,
});

const NAMED_HTML_ENTITIES: Record<string, string> = {
    amp: '&',
    apos: "'",
    gt: '>',
    lt: '<',
    nbsp: ' ',
    quot: '"',
};

/**
 * Magento widget/text fields often HTML-encode markup (`&lt;span&gt;…`).
 * Decode once so the sanitizer can keep safe tags instead of rendering them
 * as visible text via `dangerouslySetInnerHTML`.
 */
function decodeHtmlEntities(value: string): string {
    if (!value.includes('&')) {
        return value;
    }

    return value.replace(/&(#(\d+)|#x([\da-f]+)|[a-z]+);/gi, (match, _entity, decimal, hex) => {
        if (decimal) {
            return String.fromCodePoint(parseInt(decimal, 10));
        }
        if (hex) {
            return String.fromCodePoint(parseInt(hex, 16));
        }
        return NAMED_HTML_ENTITIES[match.slice(1, -1).toLowerCase()] || match;
    });
}

export function sanitizeCmsHtml(html?: string | null): string {
    if (html == null || typeof html !== 'string' || html === '') {
        return '';
    }

    return ensureHtmlImageAlts(cmsHtmlFilter.process(decodeHtmlEntities(html)));
}
